import { Fragment } from 'react';
import { NodeButton } from './NodeButton';

type Props = {
  /** The selection's placement chain: the root first, the selection last. */
  path: string[];
  labelOf: (id: string) => string;
  /** Selects an ancestor; a group shows its members. */
  onSelect: (id: string) => void;
};

/** More crumbs than this keep the root and the last two, with `…` for the ones between. */
const MAX_CRUMBS = 4;

/** Where the selection sits in the view: root › … › selected, each ancestor a button that selects it. */
export function SelectionBreadcrumb({ path, labelOf, onSelect }: Props) {
  if (path.length < 2) return null;
  const crumbs = path.length > MAX_CRUMBS ? [path[0], null, ...path.slice(-2)] : path;
  const selectedId = path[path.length - 1];
  return (
    <nav aria-label="Path to the root" className="text-xs">
      <ol className="flex flex-wrap items-center gap-x-1 gap-y-0.5">
        {crumbs.map((id, i) => (
          <Fragment key={id ?? 'more'}>
            {i > 0 && (
              <li aria-hidden className="text-zinc-400 dark:text-zinc-500">
                ›
              </li>
            )}
            <li className="flex min-w-0">
              {id === null ? (
                <span className="text-zinc-500 dark:text-zinc-400" title={`${path.length - 3} more`}>
                  …
                </span>
              ) : id === selectedId ? (
                <span
                  aria-current="location"
                  title={labelOf(id)}
                  className="max-w-40 truncate font-medium text-zinc-900 dark:text-zinc-100"
                >
                  {labelOf(id)}
                </span>
              ) : (
                <NodeButton label={labelOf(id)} truncate onClick={() => onSelect(id)} />
              )}
            </li>
          </Fragment>
        ))}
      </ol>
    </nav>
  );
}
